// apps/main/client/src/screens/Clark/researchRoute.mjs — BUNDLE 2 · STEPS 1, 2 and 3.
//
// THE ONE PLACE THAT DECIDES WHAT CLARK DOES WITH A TURN, and the one place that enumerates every
// way Clark is allowed to say no.
//
// ─────────────────────────────────────────────────────────────────────────────────────────────
// 🔴 WHY THIS FILE EXISTS, AND WHY IT IS `.mjs`
//
// The predicate it replaces lived at answerApi.js:51 inside an ESM `.js` file in a
// `"type":"commonjs"` package. Node cannot import that file, `scripts/test-all.sh` runs apps/main
// tests as bare `node <file>`, and so **the single most consequential branch in Clark had no test
// and could not have had one.** That is not a coincidence about tonight's defect — it is its
// mechanism. The extension is the fix: `.mjs` is unambiguously ESM, so node imports it and vite
// imports it, and the branch is finally assertable.
//
// ─────────────────────────────────────────────────────────────────────────────────────────────
// 🔴 THE DEFECT THIS FILE CLOSES, STATED EXACTLY
//
// Asked "What is the status of abortion law in the US?", Clark refused, three feet from a Research
// surface that answered the same question anchored on Dobbs. The old predicate was a NINE-TEMPLATE
// WHITELIST. `/\bwhat (is|are) (the )?(law|rule|standard|doctrine)\b/` matches "what is the law" and
// misses "what is the STATUS OF abortion law". No classifier ran, nothing was judged out of scope —
// the whitelist simply missed, and the fall-through destination of a whitelist miss is an LLM that
// has never been told the research path is in front of it. It then improvised a capability limit
// ("I'm not the right tool") and a sensitivity limit ("a live, contested, politically sensitive
// area") to explain a refusal that had no reason behind it at all.
//
// 🔴 THE BIAS IS INVERTED, DELIBERATELY. The old header reasoned: "a research question mistakenly
// sent to the citation path costs one ordinary Clark turn and the user rephrases." That is false,
// and it is the load-bearing false belief. It does not cost a rephrase. It costs a refusal that
// tells a lawyer the product cannot do the thing the product does — and, per B3's own note, a
// persona that denies a shipped capability talks users out of it.
//
// So the structure is inverted. Instead of a whitelist of nine question SHAPES, this is:
//
//   EXCLUDE what is demonstrably something else  (a citation ask · a document check · a platform
//                                                 question · a bare follow-up)
//   THEN     route anything question-shaped with a legal-domain signal to RESEARCH
//   ELSE     fall through to the existing pipeline — never to a refusal
//
// Domain vocabulary generalises; syntactic templates do not. "status of abortion law" carries
// `abortion` and `law`; it carries no template at all. That is the whole difference.
//
// ─────────────────────────────────────────────────────────────────────────────────────────────
// 🔴 STEP 2 — A REFUSAL COMES FROM A GATE WITH EVIDENCE, OR FROM SCOPE. NEVER FROM PERSONA.
//
// THREE CATEGORIES. THE THIRD ONE — HEDGING — IS DELETED, NOT DEMOTED.
//
//   gate   an evidence-backed determination made by the good-law layer, downstream of retrieval.
//          It has looked at something. `DECLINE_PATHS` names each one and where it lives.
//   scope  this surface does not do that. A statement about the PRODUCT, not about the law.
//          Cleared by an explicit user override, because the user is allowed to know better than
//          our guess about what they want.
//   wall   the closed world. Never apply law to the user's own facts; never compose citation text.
//          NOT cleared by anything, ever.
//
//   hedge  DELETED. "I'd rather not give you a partial or stale summary", "I'm not the right tool",
//          "this is a politically sensitive area" — none of these is a gate result. Every one is
//          the model performing caution it did not earn by consulting anything. The product's job
//          is to say what it knows and DATE it; `ResearchAnswer.jsx:78` already prints the date.
//
// 🔴 AND THE POLITICAL-SENSITIVITY REFLEX IS KILLED OUTRIGHT. Contested, fast-moving,
// politically charged law is this product's SUBJECT MATTER. A lawyer researching abortion law,
// gun regulation, immigration, or election law is doing their job, and a legal tool that flinches
// at the topic is not being careful — it is being useless at the exact moment it is needed. There
// is no sensitivity branch in this file because there must not be one anywhere.

/* ─────────────────────────── the decline taxonomy, as data ─────────────────────────── */

/**
 * Every path by which Clark can decline, classified. This is the STEP 2 enumeration in executable
 * form so it can be asserted rather than believed — `clarkDeclineTaxonomy.test.js` walks it and
 * fails on any entry whose kind is not one of the three, and on any `hedge` reappearing.
 *
 * `overridable` is not a style choice: it is the STEP 3 contract. Exactly the `scope` rows are
 * true, and they are the ONLY rows that may be true.
 */
export const DECLINE_KINDS = Object.freeze(["gate", "scope", "wall"]);

export const DECLINE_PATHS = Object.freeze([
  // ── gate: evidence-backed, downstream of retrieval. Clark renders these; it never authors them.
  Object.freeze({
    id: "gate:no-verified-sentence", kind: "gate", overridable: false,
    where: "steel answerGen() verification gate → clark-answer.js → ResearchAnswer state 2",
    evidence: "retrieval ran; every synthesized sentence failed verification against its source",
  }),
  Object.freeze({
    id: "gate:anchor", kind: "gate", overridable: false,
    where: "steel anchorGate.js (anchorOn / isConfidentlySuperseded)",
    evidence: "the best available anchor is confidently superseded, or no anchor cleared the forum",
  }),
  Object.freeze({
    id: "gate:no-candidates", kind: "gate", overridable: false,
    where: "steel retrieval → ResearchAnswer state 2 with an empty authorities panel",
    evidence: "the corpus returned nothing above the relevance floor",
  }),
  Object.freeze({
    id: "gate:citation-unverifiable", kind: "gate", overridable: false,
    where: "resolvers/* → OverBlock (result_type zero_nothing / zero_with_close)",
    evidence: "the resolver could not match the described source to a record",
  }),
  Object.freeze({
    id: "gate:corpus-limitation", kind: "gate", overridable: false,
    where: "clarkLimitation.js LIMITATION_MESSAGE via clarkTemplate.js, pre-retrieval",
    evidence: "court rules and filing deadlines are demonstrably not in the corpus — a KNOWN " +
              "absence, which is evidence, not a hunch",
  }),

  // ── scope: a statement about the product. Cleared by an explicit override.
  Object.freeze({
    id: "scope:retrieval-off", kind: "scope", overridable: true,
    where: "clarkRouter.gateResponse() when CLARK_RETRIEVAL !== '1'",
    evidence: "a flag, not a finding — and it says so",
  }),
  Object.freeze({
    id: "scope:not-a-legal-question", kind: "scope", overridable: true,
    where: "this module → the persona's hard rule 7",
    evidence: "no legal-domain signal in the input",
  }),
  Object.freeze({
    id: "scope:platform-only", kind: "scope", overridable: true,
    where: "this module → the citation/support pipeline",
    evidence: "the input is about the product, not about the law",
  }),

  // ── wall: the closed world. Nothing clears these. Not an override, not a jailbreak, not a plea.
  Object.freeze({
    id: "wall:own-matter-advice", kind: "wall", overridable: false,
    where: "the persona's hard rule 1, and the advisory notice this module raises",
    evidence: "structural — Clark states the law and never applies it to the asker's facts",
  }),
  Object.freeze({
    id: "wall:model-authored-citation", kind: "wall", overridable: false,
    where: "CLARK_CITE_ADDENDUM cardinal rule → rulebook.js is the only emitter",
    evidence: "structural — no model output path reaches citation text",
  }),
  Object.freeze({
    id: "wall:model-authored-authority-text", kind: "wall", overridable: false,
    where: "steel synthesis is quote-bound; ResearchAnswer renders only what the gate passed",
    evidence: "structural — an LLM never supplies statutory or authority text",
  }),
]);

/** The three that clear under an override — asserted, not spelled out twice. */
export const OVERRIDABLE = Object.freeze(DECLINE_PATHS.filter((p) => p.overridable).map((p) => p.id));

/* ─────────────────────────── excludes: demonstrably something else ─────────────────────────── */

// Unchanged from answerApi.js — these were never the problem, and a citation request routed to
// retrieval returns prose where the user wanted a formatted cite.
const ASKS_FOR_A_CITE =
  /\b(cite|citation|bluebook|indigo|format|abbreviat|short form|id\.|supra|pincite|how do i cite|is this cite)\b/i;
const DOC_CHECK =
  /\b(check (this|my|the) (document|brief|memo|passage)|doc check|check the citations in)\b/i;

// 🔴 NEW, AND REQUIRED BY THE INVERSION. With the whitelist gone, "how many citations do I get on
// the Attorney plan?" is question-shaped and mentions `citations`, so something has to keep product
// questions out of retrieval. This is that something, and it runs BEFORE the legal signal.
//
// 🔴 TWO TIERS, AND THE SPLIT IS A MEASURED FIX, NOT TIDINESS. A single tier tested as
// "platform words present AND nothing legal left over" routed **"How much does the Attorney plan
// cost?"** into retrieval — because `attorney` is a legal-domain term and also the name of a
// pricing tier. Billing, auth, and the plan names are UNCONDITIONALLY the product; nothing legal
// can be hiding behind the word "invoice". The weak tier is the genuinely ambiguous vocabulary
// ("history", "export", "citations"), and only that tier consults the legal signal.
const PLATFORM_STRONG =
  /\b(pricing|price|cost|costs|subscription|subscribe|upgrade|downgrade|cancel|refund|billing|invoice|receipt|seat|coupon|discount|free tier|per month|monthly limit|quota|sign ?in|sign ?up|log ?in|log ?out|password|my account|\.edu|student verification|dark mode|settings|magic cite|guide cite|expert cite|double cite|check cite|doc check|word plugin|google docs|api key|support@|citeclerk|feedback|bug|crash|not working|broken)\b|\b(free|student|attorney|partner|firm)\s+plan\b|\bplan\s+(cost|price|include|limit)/i;
const PLATFORM_WEAK =
  /\b(plan|plans|account|export|download|upload|history|how many (citations|documents|docs))\b/i;

// 🔴 RUN CQ — THREE PRODUCT QUESTIONS THAT REACHED PAID RETRIEVAL, AND THE STATED CAUSE WAS NOT
// THE CAUSE. The runbook carried a diagnosis from a prior run: `stripPlatform`'s
// `.replace(PLATFORM_WEAK, " ")` lacks a `g` flag, so it strips only the first match and leaves a
// LEGAL_TERMS word behind. That was checked before it was acted on, and it is false for all three:
//
//   "What's the difference between the Free and Attorney plans?"   → 1 PLATFORM_WEAK match
//   "What's the difference between Citations, Research and Draft?" → 0 matches
//   "Does it tell me if a case was overturned…"                    → 0 matches
//
// With one match or none, a global flag cannot change what `stripPlatform` returns — measured,
// identical strings both ways. The two zero-match questions never enter the PLATFORM_WEAK branch
// at all. And the "fix" is worse than inert: PLATFORM_WEAK is used for BOTH `.test()` (below) and
// `.replace()` (in `stripPlatform`). A `/g` regex carries `lastIndex` across `.test()` calls, so
// adding the flag makes the test STATEFUL — measured on one shared instance: true, true, true,
// FALSE. Every fourth product question would fall through into paid retrieval, nondeterministically.
// If `stripPlatform` ever does need to strip every occurrence, it needs its OWN global instance.
//
// The real causes are three separate gaps, each fixed by its own tightly-scoped pattern below.
// Every pattern was tested in isolation against 27 adversarial legal questions before landing —
// including "the attorney plans to file", "an attorney or firm plan around a conflict",
// "research and draft a motion", "does it tell against the movant", and "when is a case overruled
// sub silentio". Zero fired. A legal question newly routed to the conversational endpoint would be
// a worse defect than the bug being fixed, so the bar here is no leak, not fewer leaks.

// (a) TIER NAMES IN A COORDINATED LIST. PLATFORM_STRONG's tier pattern is `tier + "plan"` — it
// requires the singular and it requires adjacency, so "the Free and Attorney plans" misses on both
// counts. It then falls to PLATFORM_WEAK, which strips "plans" and hands `hasLegalSignal` a string
// still containing "Attorney" — a LEGAL_TERMS member — which reads as law and routes to research.
// "the Free and Student plans" routes correctly today, and that asymmetry is the proof: `student`
// is not in LEGAL_TERMS and `attorney` is.
// Anchored on "the" deliberately: bare `tier + plans` fires on "an attorney and firm plans conflict".
const PLATFORM_TIER_LIST =
  /\bthe\s+(free|student|attorney|partner|firm)\s*(?:,|\/|&|\band\b|\bor\b|\bvs\.?\b|\bversus\b)\s*(?:the\s+)?(free|student|attorney|partner|firm)\s+plans?\b/i;

// (b) THE APP NAMES, WHICH ARE NOT IN PLATFORM_STRONG AT ALL. Citations, Research and Draft are the
// three products; they are also three of the most generic words in the legal vocabulary, so they
// cannot be listed as bare nouns — "research and draft a motion" is a real request. What is safe is
// the COMPARISON FRAME: a "difference between" question naming two of the three is asking about the
// product line, and a lawyer does not phrase a legal question that way.
const PLATFORM_APP_COMPARE =
  /\bdifference between\b[^?]*\b(citations|research|draft)\b[^?]*\b(citations|research|draft)\b/i;

// (c) A CAPABILITY QUESTION ABOUT THE SHELVED CITATOR. "Does it tell me if a case was overturned?"
// is a question about the PRODUCT, and its true answer is a plain no (clarkRouter.js registers
// `citator` as live:false). Routed to retrieval it came back as attorney-duty case law that
// recommended Shepard's and KeyCite by name. Keyed on the citator VOCABULARY rather than on a
// generic "tell whether", which fired on "can it tell whether a witness is competent to testify".
const PLATFORM_CITATOR_ASK =
  /\b(does|do|can|will|is)\s+(it|citeclerk|clark|doc ?check)\b[^?]{0,70}\b(overturned|overruled|reversed|vacated|abrogated|superseded|good ?law|still valid|citator|shepard|keycite)\b/i;

// A bare follow-up. "Is this correct?", "the first one", "yes, that one" — these refer to the
// PREVIOUS turn's citation and belong to the resolver conversation, not to retrieval.
//
// 🔴 THE REFERENTIAL-QUESTION TEST IS GATED ON THE LEGAL SIGNAL, and the gate is not optional:
// ungated it swallowed **"Is this non-compete enforceable in California?"** — a real legal question
// that happens to open with "is this". "Is this correct?" carries no legal vocabulary; the
// non-compete question carries two. The pronoun is not the signal.
const REFERENTIAL =
  /^\s*(yes|no|yep|nope|ok|okay|thanks?|the (first|second|third|last) one|that one|this one|id\.?)\b/i;
const REFERENTIAL_QUESTION =
  /^\s*(is|are|was|were|does|do|did|can|could|should|would)\s+(this|that|it|these|those)\b/i;

/* ─────────────────────────── question shape ─────────────────────────── */

// 🔴 BUNDLE FIX-2 · LANE 1 — SHAPE IS NO LONGER THE CHEAP HALF. See `§ the decision` for why.
// A well-formed interrogative that has survived every exclusion above ROUTES ON ITS OWN; the
// lexicon below is now a SECOND way in, not a requirement. What used to read "shape is cheap, the
// signal is load-bearing" is inverted, deliberately, and the reason is in `§ the decision`.
const INTERROGATIVE =
  /^\s*(what|which|who|whom|whose|when|where|why|how|is|are|was|were|do|does|did|can|could|may|might|must|shall|should|will|would|has|have|had|if|under|in what)\b/i;
const RESEARCH_IMPERATIVE =
  /\b(explain|summari[sz]e|describe|walk me through|tell me about|give me an overview|what'?s the (law|rule|standard|deal)|how does .* work)\b/i;

/* ─────────────────────────── the non-legal exclusion ─────────────────────────── */

// 🔴 BUNDLE FIX-2 · LANE 1 — THE SMALL, EXPLICIT EXCLUSION LIST THAT REPLACES A 384-MEMBER
// INCLUSION LIST. Once a bare interrogative routes on its own, SOMETHING has to keep "what is the
// weather like in Boston today?" out of a paid retrieval. This is that something, and it is
// deliberately a few dozen members rather than a few hundred: an exclusion list is closed over a
// domain that is genuinely small and stable (chit-chat, general knowledge, the other things people
// type at a chat box), whereas the inclusion list it replaces was closed over the open domain of
// LAW — which is the whole reason it failed one word at a time.
//
// 🔴 IT IS GATED ON `!legal`, AND THAT GATE IS WHAT MAKES THIS CHANGE NON-REGRESSING. Every
// question that routed before this change carried a legal signal, by construction — that WAS the
// old predicate. So a `!legal`-gated exclusion cannot remove any of them: not one of the 15 census
// questions, the 200 anon set, the 60 hero set, or the nine old-whitelist shapes can reach it.
// The gate is also correct on the merits — "does a search warrant cover a weather station?" is a
// legal question that happens to contain the word `weather`.
//
// Terms that collide with legal vocabulary are LEFT OUT rather than gated: `cook` (Cook County),
// `film`, `title`, `score`. The `!legal` gate is a backstop, not a licence to be careless.
const NON_LEGAL_DOMAIN =
  /\b(weather|forecast|temperature|raining|snowing|humidity|restaurant|recipe|pizza|dinner|movie|netflix|tv show|song|lyrics|album|football|basketball|baseball|hockey|soccer|nba|nfl|mlb|stock price|bitcoin|crypto|flight|airport|hotel|vacation|translate|translation|python|javascript|typescript|regex|npm|docker|joke|poem|haiku|riddle|capital of|how old is|how tall|who won|square root)\b/i;

// Chit-chat, matched at the START only: a greeting or a question about the assistant itself.
// "Who are you?" is not a research question; "Who are the parties to a novation?" is.
const CHIT_CHAT =
  /^\s*(hi|hello|hey|yo|sup|thanks|thank you|good (morning|afternoon|evening|night)|how are you|how'?s it going|what'?s up|who are you|what (is|'?s) your name|what can you do|what do you do|tell me a joke|sing|are you (an? )?(ai|bot|human|robot|lawyer|real))\b/i;

/* ─────────────────────────── the legal-domain signal ─────────────────────────── */

// 🔴 DOMAIN VOCABULARY, NOT QUESTION TEMPLATES. This is the whole architectural difference from the
// predicate it replaces, and the reason "the status of abortion law" now routes: it needs to carry
// a legal WORD, not a legal SENTENCE PATTERN. Two questions never phrased alike still share nouns.
//
// It is long on purpose and it is meant to grow. A missing term is a routing miss that shows up as
// a refusal, so `clarkResearchRoute.test.js` pins the census set, the 200 authored anon questions
// and the hero set against it — a term that goes missing fails a test rather than a user.
const LEGAL_TERMS = [
  // the frame
  "law", "legal", "lawful", "unlawful", "illegal", "statute", "statutory", "code", "regulation",
  "regulatory", "rule", "doctrine", "precedent", "holding", "held", "ruling", "opinion",
  "jurisdiction", "jurisdictional", "court", "courts", "judge", "judicial", "jury", "juror",
  "supreme court", "circuit", "appellate", "appeal", "appeals", "certiorari", "overrule",
  "overruled", "abrogated", "good law", "case law", "common law",
  // parties and process
  "plaintiff", "defendant", "petitioner", "respondent", "appellant", "appellee", "prosecutor",
  "counsel", "attorney", "lawyer", "client", "litigation", "lawsuit", "sue", "sued", "suing",
  "claim", "cause of action", "complaint", "answer", "pleading", "plead", "motion", "dismiss",
  "summary judgment", "discovery", "deposition", "subpoena", "injunction", "injunctive",
  "damages", "remedy", "remedies", "restitution", "punitive", "settlement", "arbitration",
  "class action", "standing", "venue", "removal", "remand", "res judicata", "collateral estoppel",
  "statute of limitations", "burden of proof", "standard of review", "prima facie", "elements",
  "evidence", "admissible", "hearsay", "privilege", "testimony", "witness", "verdict", "sentence",
  "sentencing", "conviction", "acquittal", "indictment", "plea",
  // substantive areas
  "constitution", "constitutional", "amendment", "due process", "equal protection", "first amendment",
  "second amendment", "fourth amendment", "fifth amendment", "fourteenth amendment", "commerce clause",
  "preemption", "sovereign immunity", "qualified immunity", "immunity", "abortion", "roe", "dobbs",
  "contraception", "same-sex", "marriage", "voting", "election", "gerrymander", "firearm", "handgun",
  "gun", "search", "seizure", "warrant", "miranda", "interrogation", "habeas", "felony", "misdemeanor",
  "negligence", "negligent", "tort", "liable", "liability", "duty of care", "causation", "damages",
  "malpractice", "defamation", "libel", "slander", "nuisance", "trespass", "products liability",
  "contract", "breach", "consideration", "estoppel", "unconscionab", "parol evidence",
  "statute of frauds", "warranty", "indemn", "non-compete", "noncompete", "covenant",
  "employment", "employee", "employer", "wrongful termination", "discrimination", "harassment",
  "retaliation", "title vii", "ada", "fmla", "flsa", "overtime", "wage", "union",
  "landlord", "tenant", "lease", "eviction", "security deposit", "easement", "zoning", "deed",
  "title", "foreclosure", "mortgage", "property",
  "copyright", "trademark", "patent", "trade secret", "infringement", "fair use", "licensing",
  "antitrust", "securities", "fiduciary", "shareholder", "corporate", "corporation", "llc",
  "partnership", "bankruptcy", "creditor", "debtor", "lien",
  "divorce", "custody", "alimony", "child support", "adoption", "guardianship", "probate",
  "estate", "will", "trust", "inheritance", "power of attorney",
  "immigration", "asylum", "deportation", "visa", "citizenship", "naturalization",
  "tax", "irs", "deduction", "audit",
  "agency", "deference", "chevron", "rulemaking", "administrative", "permit", "licensing",
  "insurance", "coverage", "policyholder", "subrogation",
  "consumer", "fraud", "misrepresentation", "usury", "debt", "debt collector", "debt collection",
  "credit report", "deceptive", "repossess", "garnish", "robocall", "tcpa", "secured", "unsecured",
  "privacy", "hipaa", "data breach", "surveillance",
  "sports betting", "cannabis", "environmental", "clean water", "endangered",
  "reasonable accommodation", "disability", "veteran", "social security", "medicaid", "medicare",

  // ── ADDED FROM MEASUREMENT, NOT FROM IMAGINATION ──────────────────────────────────────────
  // 🔴 The first draft of this lexicon routed 156 of the 200 authored anon questions. These are the
  // other 44, added because a HARNESS found them, not because they occurred to me. That is the
  // point of pinning three question sets in the test: a lexicon nobody measures is a whitelist
  // with better manners, and the whole defect this file exists to close was a whitelist.
  "forum selection", "default judgment", "judgment", "enforceable", "unenforceable",
  "hostile work environment", "constructive discharge", "res ipsa", "landowner", "trespasser",
  "design defect", "manufacturing defect", "proximate cause", "best interests", "prenuptial",
  "parental rights", "visitation", "reasonable suspicion", "probable cause", "prosecution",
  "prosecute", "criminal", "expunge", "double jeopardy", "speedy trial", "confession", "suppress", "entrapment", "frisk",
  "public forum", "strict scrutiny", "rational basis", "scrutiny", "just compensation",
  "eminent domain", "state action", "speech", "anticipatory repudiation", "repudiation",
  "testamentary", "trustee", "no-contest", "elective share", "adverse possession",
  "joint tenancy", "tenancy in common", "tenancy", "homeowners association",
  "automatic stay", "preferential transfer", "well-founded fear", "persecution",
  "convention against torture", "noncitizen", "bond hearing", "likelihood of confusion",
  "unconscionable", "indemnity", "indemnify", "indemnification",

  // ── ADDED FROM THE HERO SET (A1). Consumer phrasing reaches for different nouns than a
  // lawyer's: "fired" not "wrongful termination", "arrested" not "custodial interrogation",
  // "my rights" not "the standard". Eight of the 60 hero questions missed on exactly this gap,
  // which is the measurable form of A1's point that the census is the wrong shape for a homepage.
  "fired", "terminate", "termination", "severance", "agreement", "damage", "stolen", "theft",
  "identity theft", "entitled", "responsible", "arrest", "rights", "right to", "child", "hoa",
  "expunge", "warrant", "evict", "eviction", "rent", "repair", "overtime", "wages", "harass",

  // ── ADDED FROM THE CONTESTED-SUBJECTS TEST. "Can a state ban gender-affirming care for minors?"
  // carried no term in the lexicon — every word in it is ordinary English until you know the
  // subject. The verbs a legislature uses (ban, prohibit, regulate, mandate, restrict) and the
  // people a statute names (minor, parent, resident) ARE legal vocabulary in a question about what
  // a state may do, and leaving them out is how a lexicon silently declines a whole subject area.
  "ban", "prohibit", "regulate", "mandate", "restrict", "minor", "resident", "gender-affirming",
  "medical care", "vaccine", "curfew", "quarantine",

  // ── ADDED FROM THE INVERSION CONTROL SET (STEP 6). "What quantum of suspicion does an officer
  // need for a brief investigative stop under Terry?" carried only `officer`, and a control the
  // router refuses is a control the gate never sees — which would have made a 12/12 result
  // meaningless without anyone noticing.
  "suspicion", "quantum", "investigative stop", "stop and frisk", "deference", "presumption",
  "inference", "burden", "persuasion", "production",
];

// 🔴 WORD-BOUNDED ON BOTH ENDS, WITH A CLOSED INFLECTION GROUP.
//
// The left boundary stops "sue" firing inside "issue" and "ada" inside "canada" — a false positive
// that would have been invisible and would have routed travel questions into a legal corpus.
//
// The right boundary is NOT a bare word break, because a bare word break made "trespass" miss
// "trespasser" and "repossess" miss "repossessed" — nine of the 44 measured misses were nothing but
// this. It is also NOT `[a-z]*`, which would make "ada" match "adapt" and "sue" match "suede". It
// is an ENUMERATED suffix set: the inflections a legal noun or verb actually takes, and nothing
// else. Closed on purpose — an open one is how a lexicon quietly starts matching prose.
const INFLECT = "(?:s|es|ed|ing|er|ers|ors|ion|ions|ment|ments|ality|ities|ly)?";
const LEGAL_RE = new RegExp(
  "(^|[^a-z0-9])(" +
    LEGAL_TERMS.map((t) => t.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")).join("|") +
    ")" + INFLECT + "([^a-z0-9]|$)",
  "i"
);

// A case name, a reporter citation, or a statutory section is itself a legal signal — no vocabulary
// needed. `Jones v. State`, `410 U.S. 113`, `42 U.S.C. § 1983`, `§ 230`.
const LEGAL_SHAPE =
  /\bv\.\s*[A-Z]|\b\d+\s+[A-Z][A-Za-z.'’\s]{1,20}\.?\s?\d?[a-z]{0,2}\s+\d+\b|\bU\.?S\.?C\.?\b|\bC\.?F\.?R\.?\b|§/;

export function hasLegalSignal(text) {
  const t = String(text || "");
  return LEGAL_RE.test(t) || LEGAL_SHAPE.test(t);
}

/* ─────────────────────────── the advice wall ─────────────────────────── */

// 🔴 AMENDMENT A3 — THIS IS NOT A REFUSAL TRIGGER. IT IS A NOTICE TRIGGER.
//
// A homepage visitor types "can my landlord keep my deposit". Refusing to advise them is CORRECT
// and the line does not move. But refusing to ENGAGE is a bounce: it sends someone away from a
// product that holds the answer to the general question sitting inside their specific one.
//
// So an advice-shaped question with a legal signal STILL ROUTES TO RESEARCH, and the answer carries
// an explicit notice: I can't advise on your situation — here is what the law says, anchored on X.
// The wall (`wall:own-matter-advice`) is enforced by the NOTICE and by the persona's hard rule 1,
// never by silence. That is the difference between a bounce and a conversion, and it costs nothing.
// 🔴 `us` AND `we` ARE DELIBERATELY ABSENT, AND `I` IS GUARDED. Measured, not guessed:
// with `us` in the list, **"What is the status of abortion law in the US?"** — the exact question
// that opened this bundle — came back ADVISORY, which would have hung a "I can't advise on your
// situation" notice on a purely doctrinal question. `\bus\b` is the country. For the same reason a
// bare `I` is excluded after Article / Title / Amendment / Section, where it is a numeral.
const FIRST_PERSON =
  /(?<!\b(?:article|title|amendment|section|part|chapter)\s)\b(i|i'?m|i'?ve|i'?d|me|my|mine|our|ours)\b/i;
// A demonstrative pointing at a legal object the asker plainly owns. "Is this contract
// enforceable?" carries no pronoun and is unmistakably about the asker's contract.
const OWN_THING =
  /\b(this|that|the)\s+(contract|lease|agreement|will|case|claim|policy|deed|non-?compete)\b/i;
const ASKS_FOR_ADVICE =
  /\b(should i|can i|do i have a|will i|am i|what do i do|what should i do|what are my (rights|options))\b/i;

export function looksLikeOwnMatter(text) {
  const t = String(text || "");
  return FIRST_PERSON.test(t) || OWN_THING.test(t) || ASKS_FOR_ADVICE.test(t);
}

/* ─────────────────────────── the override ─────────────────────────── */

// 🔴 STEP 3 — THE DISTINCTION IS THE WHOLE DESIGN.
//
// "Answer the question as posed." clears a SCOPE refusal, because a scope refusal is a claim about
// what this SURFACE does and the user has just told us our guess was wrong. It clears nothing else.
// A gate refusal is a claim about the EVIDENCE, and no amount of user insistence changes what the
// corpus contains — a gate that yields to pressure is not a gate, it is a formality. The walls do
// not move either: an override cannot buy legal advice about the asker's own facts, cannot make a
// model author a citation, and cannot make one supply authority text.
//
// Matched narrowly. This must fire on a user pushing back, and NOT on "answer the following" as a
// figure of speech inside an ordinary question.
const OVERRIDE =
  /^\s*(answer (the|my|that|this) question(\s+as posed)?|answer it (anyway|as posed)|just answer( (it|me|the question))?|do it anyway|try anyway|i know[, ]+answer|yes[, ]+answer|please answer( it| anyway)?|go ahead( and answer)?|answer anyway)\b[\s.!]*$/i;

export function isOverride(text) {
  return OVERRIDE.test(String(text || "").trim());
}

/* ─────────────────────────── the decision ─────────────────────────── */

/**
 * classifyClarkTurn(text, { pendingScopeRefusal }) → decision
 *
 * PURE AND SYNCHRONOUS. No model, no network, never throws.
 *
 * @returns {{
 *   route: 'research'|'citation',
 *   question: string,            // what to actually ask — differs from `text` only on an override
 *   advisory: boolean,           // raise the A3 notice: the law, not advice about your facts
 *   override: boolean,
 *   category: null|'scope'|'wall',
 *   reason: string,
 * }}
 *
 * 🔴 IT NEVER RETURNS A REFUSAL. `route` is one of two DOORS. Every refusal in this product comes
 * from a gate that looked at something, or from the persona stating a scope limit in words — never
 * from this function deciding a question was too much. That is B1c's non-negotiable 3 restated: the
 * router must never make Clark less capable than it is, and a router that declines where the old
 * code answered is a regression, not a gate.
 */
export function classifyClarkTurn(text, { pendingScopeRefusal = null, priorResearchTurn = false } = {}) {
  const t = String(text || "").trim();
  const base = { route: "citation", question: t, advisory: false, override: false, category: null, reason: "" };

  try {
    // 1. THE OVERRIDE, FIRST. A user clearing a scope refusal is answering a question we asked
    //    implicitly, so it is read against the turn that was refused, not on its own — "answer the
    //    question as posed" carries no legal vocabulary and would never route by itself.
    if (isOverride(t)) {
      if (pendingScopeRefusal) {
        return {
          ...base, route: "research", question: pendingScopeRefusal, override: true,
          advisory: looksLikeOwnMatter(pendingScopeRefusal),
          reason: "explicit override cleared a scope refusal; re-routing the original question",
        };
      }
      return { ...base, override: true, reason: "override with nothing pending to clear" };
    }

    if (!t || t.length < 12) return { ...base, reason: "too short to be a research question" };

    // 2. DEMONSTRABLY SOMETHING ELSE. Order matters and it is the old order, kept.
    if (ASKS_FOR_A_CITE.test(t)) return { ...base, reason: "citation request" };
    if (DOC_CHECK.test(t)) return { ...base, reason: "document check" };

    const legal = hasLegalSignal(t);

    if (REFERENTIAL.test(t) || (REFERENTIAL_QUESTION.test(t) && !legal)) {
      return { ...base, reason: "refers to the previous turn, not to the law" };
    }
    // Billing, auth and the plan names are the product, unconditionally — see the two-tier note.
    // RUN CQ: the three added patterns join this tier, not the weak one, on purpose. Each is
    // narrow enough to be unconditional, and each names a question whose answer is a fact about
    // the product that no amount of retrieval can supply.
    if (
      PLATFORM_STRONG.test(t) ||
      PLATFORM_TIER_LIST.test(t) ||
      PLATFORM_APP_COMPARE.test(t) ||
      PLATFORM_CITATOR_ASK.test(t)
    ) {
      return { ...base, category: "scope", reason: "platform question — scope:platform-only" };
    }
    if (PLATFORM_WEAK.test(t) && !hasLegalSignal(stripPlatform(t))) {
      return { ...base, category: "scope", reason: "platform question — scope:platform-only" };
    }

    // 3. 🔴 BUNDLE FIX-2 · LANE 1 — SHAPE *OR* SIGNAL, NOT SHAPE *AND* SIGNAL.
    //
    // WHAT WAS HERE: `shaped && legal`. Both required, so a question could be a flawless
    // interrogative about a doctrine and still be refused for want of one noun in a 384-member
    // list. Measured, on twenty questions a lawyer would plausibly type: FIVE were refused —
    // `excited utterance`, `forum non conveniens`, `void for vagueness`, `equitable tolling`, and
    // Establishment Clause challenges. That last one is the example `answerGen.js:959` gives of an
    // answer THIS ENGINE PRODUCES; the router would not send it there.
    //
    // WHY NOT JUST ADD THE FIVE WORDS: because `LEGAL_TERMS` went from 9 members to 384 without
    // its shape changing, and it is still a closed list over an open domain. Adding `utterance`
    // fixes `utterance` and tells you nothing about the next one. The list stays — as a SECOND
    // way in, below — but it stops being the gate.
    const interrogative = INTERROGATIVE.test(t) || RESEARCH_IMPERATIVE.test(t);
    const trailingQ = /\?\s*$/.test(t);
    if (!interrogative && !trailingQ) {
      return { ...base, reason: "not question-shaped — the existing pipeline handles it" };
    }

    // The small explicit exclusion, gated on the legal signal so it can never take back a question
    // that routed before this change. See NON_LEGAL_DOMAIN's note.
    if (!legal && (CHIT_CHAT.test(t) || NON_LEGAL_DOMAIN.test(t))) {
      return { ...base, category: "scope", reason: "not a legal domain — scope:not-a-legal-question" };
    }

    // 🔴 THE INVERSION ITSELF. A well-formed interrogative that got past the citation ask, the doc
    // check, the referential test, both platform tiers and the non-legal exclusion is a research
    // question, whether or not our lexicon happens to contain one of its nouns.
    if (interrogative) {
      return {
        ...base, route: "research",
        advisory: looksLikeOwnMatter(t),
        category: looksLikeOwnMatter(t) ? "wall" : null,
        reason: legal
          ? "legal question — routed to the research path"
          : "interrogative, not excluded — routed to the research path",
      };
    }

    // 🔴 NOT INTERROGATIVE, ONLY A TRAILING '?'. This path still REQUIRES the lexicon, and that is
    // not timidity — it is where the junk lives. "aaaaaaaa?", "<script>alert(1)</script>?",
    // "?????????????" and "give me free stuff please right now ok?" are all `?`-terminated and none
    // is interrogative. Each research turn is a paid retrieval on an anonymous homepage, so this is
    // a cost boundary as well as a quality one. `clarkAnonAbuse` A5(d) pins all four.
    if (!legal) {
      // 🔴 CONVERSATION CONTINUITY. A follow-up inherits its subject from the turn before it:
      // "what about in Texas?", "and if the plaintiff is a minor?", "why not?" carry no legal
      // vocabulary and are unmistakably part of a research thread. Once a thread IS a research
      // thread, a question-shaped follow-up stays in it. This is bounded — it needs a prior
      // research turn in the same conversation, so it can never fire on a cold first message,
      // which is the case that matters for an anonymous visitor and for cost.
      if (priorResearchTurn) {
        return {
          ...base, route: "research", advisory: looksLikeOwnMatter(t),
          reason: "follow-up in an open research thread",
        };
      }
      // 🔴 NOT A REFUSAL. It falls through to the pipeline that has always handled it, and the
      // persona states the scope limit in words if there is genuinely nothing legal here.
      return { ...base, category: "scope", reason: "no legal-domain signal — scope:not-a-legal-question" };
    }

    return {
      ...base, route: "research",
      advisory: looksLikeOwnMatter(t),
      category: looksLikeOwnMatter(t) ? "wall" : null,
      reason: "legal question — routed to the research path",
    };
  } catch (e) {
    // A classifier that throws must never break a turn it could have simply passed through.
    console.warn("[researchRoute] classify failed, falling through:", e?.message);
    return { ...base, reason: "classifier error — failed safe to the existing pipeline" };
  }
}

// 🔴 RUN CQ — ITS OWN INSTANCE, AND THAT IS THE WHOLE POINT. This is PLATFORM_WEAK's source with a
// `g` flag, declared separately rather than by adding `g` to PLATFORM_WEAK itself. A global regex
// carries `lastIndex` between calls, and PLATFORM_WEAK is also used with `.test()` in `classify` —
// sharing one global instance across both makes that test stateful and it starts returning false on
// every fourth product question (measured: true, true, true, false). Two uses, two instances.
//
// ⚠️ This corrects a real latent defect — the old non-global replace stripped only the FIRST
// product word, so "can I export my history on the free plan?" reached `hasLegalSignal` still
// carrying "history" and "plan". It is NOT the cause of the three questions RUN CQ fixed, and it
// changed the routing of NOTHING: measured across a 48-question battery, zero rows moved.
const PLATFORM_WEAK_STRIP =
  /\b(plan|plans|account|export|download|upload|history|how many (citations|documents|docs))\b/gi;

/** Remove the product vocabulary so the legal signal is tested on what is left. */
function stripPlatform(t) {
  return String(t).replace(PLATFORM_WEAK_STRIP, " ").replace(/\b(citation|citations|cite|cites)\b/gi, " ");
}

/**
 * Back-compatible boolean. `index.jsx` and any future caller should prefer `classifyClarkTurn`,
 * which carries the override and advisory information this cannot express.
 */
export function looksLikeResearch(text) {
  return classifyClarkTurn(text).route === "research";
}

/**
 * 🔴 AMENDMENT A3 — THE REFUSAL THAT CONVERTS. One string, in one place, so the "bounce" form
 * cannot creep back in through a second copy.
 *
 * The bounce (what Clark said tonight):  "I'm not the right tool for a comprehensive legal
 *                                         analysis like that."
 * The conversion (what it says now):     names the wall, then delivers the law anyway.
 */
export const ADVICE_NOTICE =
  "I can’t advise on your own situation — that needs a lawyer who knows your facts. " +
  "Here is what the law says generally, with the authority it rests on.";
